import React, { useEffect, useMemo } from 'react';
import Container from '@common/components/container';
import { TopNavigationBar } from '@common/components/navigation-bar';
import { Route, useHistory, useParams, useRouteMatch } from 'react-router';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import { Switch } from '@common/components/router';
import { Button } from '@common/components/button';
import { useQueryParam } from '@common/hooks/url';
import { EvaluationFormSettings } from './evaluation-form-settings';
import { createInitialValues, normalizeIconPath } from './utils';
import { AlertService } from '@common/services/alert';
import { FormIcon } from '@modules/forms/components/form-icon';
import {
  APIEvaluationForm,
  FormsData,
  Question,
  EvaluationFormPayload,
  TypeAttachment,
} from '@modules/performance/types';
import { HookFormWrapper } from '@common/hooks/use-hook-form';
import { Select } from '@common/components/form';
import {
  EPerformanceFormQuestionType,
  EvaluationFormTypes,
} from '@common/definitions';
import { FormQuestions } from './forms-question';
import { sortBy } from 'lodash';
import swap from '@common/utils/swap';
import {
  usePerformanceStore,
  withPerformanceStore,
} from '@modules/performance/store/store';
import Spinner from '@common/components/spinner';
import { FormForm } from './form/form';

type Params = {
  id?: string;
  componentId?: string;
};

const EvaluationFormContainer = () => {
  const {
    form,
    loading,
    handleCreatePerformanceForm,
    handleUpdatePerformanceForm,
    handleUpdateComponent,
    handleUpdateScreenOrder,
    handleSaveScreenOrder,
    handleCreateQuestion,
    handleDeleteQuestion,
    handleDuplicateQuestion,
    drafts,
    clearDrafts,
    categories,
  } = usePerformanceStore();

  const { t } = useTranslation();
  const match = useRouteMatch();
  const params = useParams<Params>();
  const { networkId } = match.params as { networkId: string };
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const baseUrl = networkId ? '/networks/:id' : '/admin';
  const back = useQueryParam('back');
  const formType = useQueryParam('type');
  const history = useHistory();

  const fns = useMemo(
    () => ({
      createQuestion: async (question: Question) => {
        const { data } = await handleCreateQuestion(form.id, question);
        history.push(
          `${basePath}/performance/forms/${form.id}/edit/components/${
            data.components[data.components.length - 1].id
          }`
        );
      },
      openQuestion: (id: string) => {
        history.push(
          `${basePath}/performance/forms/${params.id}/edit/components/${id}`
        );
      },
      deleteQuestion: async (id: string) => {
        await handleDeleteQuestion(form.id!, id);
        const currentIndex =
          form.components?.findIndex((component) => component.id === id) || 0;
        const newScreenId =
          form.components?.[currentIndex ? currentIndex - 1 : 1]?.id;
        history.push(
          `${basePath}/performance/forms/${form.id}/edit/components/${newScreenId}`
        );
      },
      duplicateQuestion: async (id: string) => {
        const data = await handleDuplicateQuestion(form.id!, id);
        const previousIds = (form?.components || []).map((c) => c.id);
        const newComponent = (data as any).components.find(
          (c: any) => !previousIds.includes(c.id)
        );
        if (data) {
          history.push(
            `${basePath}/performance/forms/${form.id}/edit/components/${newComponent.id}`
          );
        }
      },
      orderQuestions: (id: string, index: number) => {
        if (!form || !form.components) return;
        const order = sortBy(form.components, ['index']).map(
          (component) => component.id
        );
        const newOrder = swap(order, index, order.indexOf(id));

        handleUpdateScreenOrder(form.id, newOrder);
      },
      saveOrderQuestions: async (order: string[]) => {
        if (!form?.id) return;
        await handleSaveScreenOrder(form.id, order);
      },
    }),
    [
      form,
      params.id,
      history,
      basePath,
      t,
      handleUpdateScreenOrder,
      handleSaveScreenOrder,
    ]
  );

  const onSubmit = async (newValues: any): Promise<void> => {
    const omit = [
      'employeeVisibility',
      'text',
      'description',
      'manager',
      'employee',
      'answer_required',
      'category_ids',
      'answer_visible',
      'content_components',
      'impact_of_loss',
      'retention_risk',
    ];
    const updatePayload = {
      ...(R.omit(omit, newValues) as EvaluationFormPayload),
      icon: normalizeIconPath(newValues.icon),
    } as EvaluationFormPayload;

    if (params.id && form && !params.componentId) {
      await handleUpdatePerformanceForm(form.id, updatePayload);
      AlertService.success(t('performance:succesfully_updated_the_form'));
      return;
    }

    if (form && form.components) {
      try {
        const draftEntries = Object.entries(drafts || {});
        let singleNewId: string | undefined;
        let newFocusedIdAfterBatch: string | undefined;
        // let idMappings: Array<{ oldId: string; newId: string }> = [];
        if (draftEntries.length > 0) {
          const mappings = await Promise.all(
            draftEntries.map(async ([componentId, componentValues]) => {
              const comp = form.components?.find((c) => c.id === componentId);
              if (!comp) return undefined;

              const draftVals = componentValues as any;
              const getVal = (key: string, fallback: any) => (typeof draftVals[key] !== 'undefined'
                  ? draftVals[key]
                  : fallback);

              if (comp.type === EPerformanceFormQuestionType.COMPETENCIES) {
                const selectedIdsRaw = getVal(
                  'category_ids',
                  comp.settings?.category_ids || []
                );
                const totalCategoryCount = Array.isArray(categories)
                  ? categories.length
                  : 0;
                const selectedIds = Array.isArray(selectedIdsRaw)
                  ? selectedIdsRaw
                  : [];
                const categoryIdsToSend =
                  totalCategoryCount > 0 &&
                  selectedIds.length === totalCategoryCount
                    ? []
                    : selectedIds;

                const payload = {
                  type: comp.type,
                  settings: {
                    categoryIds: categoryIdsToSend,
                  },
                } as any;
                const res = await handleUpdateComponent(
                  form.id,
                  componentId,
                  payload as any,
                  { silent: true }
                );
                return {
                  oldId: componentId,
                  newId: res?.data?.id || componentId,
                };
              }

              if (comp.type === EPerformanceFormQuestionType.CONTENT_MODULE) {
                const contentComponents = getVal(
                  'content_components',
                  comp.settings?.content_components || []
                );

                const settings = Array.isArray(contentComponents)
                  ? contentComponents.map((item: any, index: number) => {
                      const parameters: any = {};

                      if (
                        item.type === TypeAttachment.TEXT &&
                        item.parameters?.text
                      ) {
                        parameters.text = item.parameters.text;
                      }

                      if (
                        [
                          TypeAttachment.IMAGE,
                          TypeAttachment.VIDEO,
                          TypeAttachment.PDF,
                        ].includes(item.type)
                      ) {
                        const attachmentId =
                          item.parameters?.attachmentId ||
                          item.parameters?.attachment_id ||
                          item.parameters?.attachment?.id;
                        if (attachmentId) {
                          parameters.attachmentId = attachmentId;
                        }
                      }

                      if (
                        item.type === TypeAttachment.EXTERNAL_VIDEO &&
                        item.parameters?.attachment
                      ) {
                        parameters.attachment = item.parameters.attachment;
                      }

                      return {
                        type: item.type,
                        index,
                        parameters,
                      };
                    })
                  : [];

                const payload = {
                  type: comp.type,
                  settings,
                } as any;

                const res = await handleUpdateComponent(
                  form.id,
                  componentId,
                  payload as any,
                  { silent: true }
                );
                return {
                  oldId: componentId,
                  newId: res?.data?.id || componentId,
                };
              }

              if (comp.type === EPerformanceFormQuestionType.TALENT_REVIEW) {
                const payload = {
                  type: comp.type,
                  settings: {
                    impact_of_loss: getVal('impact_of_loss', comp.settings?.impact_of_loss || false),
                    retention_risk: getVal('retention_risk', comp.settings?.retention_risk || false),
                  },
                } as any;
                const res = await handleUpdateComponent(
                  form.id,
                  componentId,
                  payload as any,
                  { silent: true }
                );
                return {
                  oldId: componentId,
                  newId: res?.data?.id || componentId,
                };
              }

              const descVal = getVal('description', comp.settings?.description);
              const hasDescription =
                typeof descVal !== 'undefined' && String(descVal).trim() !== '';

              const managerVal = getVal(
                'manager',
                comp.settings?.visibility?.manager || false
              );
              const visibilityDraft: any = {
                manager: managerVal,
                employee: getVal(
                  'employee',
                  comp.settings?.visibility?.employee || false
                ),
              };
              if (managerVal) {
                visibilityDraft.answer_visible = getVal(
                  'answer_visible',
                  comp.settings?.visibility?.answer_visible || false
                );
              }

              const payload = {
                type: comp.type,
                settings: {
                  text: getVal('text', comp.settings?.text || ''),
                  ...(hasDescription ? { description: String(descVal) } : {}),
                  answer_required: getVal(
                    'answer_required',
                    comp.settings?.answer_required || false
                  ),
                  visibility: visibilityDraft,
                },
              } as any;
              const res = await handleUpdateComponent(
                form.id,
                componentId,
                payload as any,
                { silent: true }
              );
              return {
                oldId: componentId,
                newId: res?.data?.id || componentId,
              };
            })
          );
          const focusedMapping = (mappings || []).find(
            (m) => m && m.oldId === params.componentId
          ) as { oldId: string; newId: string } | undefined;
          newFocusedIdAfterBatch = focusedMapping?.newId;
        } else if (params.componentId) {
          const currentComponent = form.components?.find(
            (comp) => comp.id === params.componentId
          );
          if (currentComponent) {
            const componentValues = newValues as any;
            const descVal = componentValues.description;
            const hasDescription =
              typeof descVal !== 'undefined' && String(descVal).trim() !== '';

            const managerVal = componentValues.manager || false;
            const visibilitySingle: any = {
              manager: managerVal,
              employee: componentValues.employee || false,
            };
            if (managerVal) {
              visibilitySingle.answer_visible =
                componentValues.answer_visible || false;
            }

            const payload = {
              type: currentComponent.type,
              settings:
                currentComponent.type ===
                EPerformanceFormQuestionType.COMPETENCIES
                  ? {
                      categoryIds: (() => {
                        const totalCategoryCount = Array.isArray(categories)
                          ? categories.length
                          : 0;
                        const selectedIds = Array.isArray(
                          componentValues.category_ids
                        )
                          ? componentValues.category_ids
                          : [];
                        return totalCategoryCount > 0 &&
                          selectedIds.length === totalCategoryCount
                          ? []
                          : selectedIds;
                      })(),
                    }
                  : currentComponent.type ===
                    EPerformanceFormQuestionType.CONTENT_MODULE
                  ? Array.isArray(componentValues.content_components)
                    ? componentValues.content_components.map(
                        (item: any, index: number) => {
                          const parameters: any = {};
                          if (
                            item.type === TypeAttachment.TEXT &&
                            item.parameters?.text
                          ) {
                            parameters.text = item.parameters.text;
                          }
                          if (
                            [
                              TypeAttachment.IMAGE,
                              TypeAttachment.VIDEO,
                              TypeAttachment.PDF,
                            ].includes(item.type)
                          ) {
                            const attachmentId =
                              item.parameters?.attachmentId ||
                              item.parameters?.attachment_id ||
                              item.parameters?.attachment?.id;
                            if (attachmentId) {
                              parameters.attachmentId = attachmentId;
                            }
                          }

                          if (
                            item.type === TypeAttachment.EXTERNAL_VIDEO &&
                            item.parameters?.attachment
                          ) {
                            parameters.attachment = item.parameters.attachment;
                          }

                          return {
                            type: item.type,
                            index,
                            parameters,
                          };
                        }
                      )
                    : []
                  : currentComponent.type ===
                    EPerformanceFormQuestionType.TALENT_REVIEW
                  ? {
                      impact_of_loss: componentValues.impact_of_loss || false,
                      retention_risk: componentValues.retention_risk || false,
                    }
                  : {
                      text: componentValues.text || '',
                      ...(hasDescription
                        ? { description: String(descVal) }
                        : {}),
                      answer_required: componentValues.answer_required || false,
                      visibility: visibilitySingle,
                    },
            } as any;
            const res = await handleUpdateComponent(
              form.id,
              currentComponent.id,
              payload
            );
            singleNewId = res?.data?.id || currentComponent.id;
          }
        }

        await handleUpdatePerformanceForm(form.id, updatePayload);
        AlertService.success(t('performance:succesfully_updated_the_form'));
        if (params.componentId) {
          // If a component id changed during updates, try to navigate to the new id.
          const targetId =
            typeof newFocusedIdAfterBatch !== 'undefined'
              ? newFocusedIdAfterBatch
              : typeof singleNewId !== 'undefined'
              ? singleNewId
              : params.componentId;
          history.push(
            `${basePath}/performance/forms/${form.id}/edit/components/${targetId}`
          );
        }
        if (clearDrafts) {
          clearDrafts();
        }
      } catch (error: any) {
        AlertService.forStatus(error.status_code, {
          warning: t('performance:could_not_update_the_form'),
          error: t('performance:could_not_update_the_form'),
        });
      }
      return;
    }

    if (!params.id) {
      await handleCreatePerformanceForm({
        ...(newValues as EvaluationFormPayload),
        icon: normalizeIconPath(newValues.icon),
      });
    }
  };

  useEffect(() => {
    if (params.id && form) {
      (async () => {
        try {
          createInitialValues(form as FormsData);
        } catch (error: any) {
          AlertService.forStatus(error.status_code, {
            error: t('performance:form_fetch_error'),
          });
        }
      })();
    }
  }, [form, params.id]);

  return (
    <Container name="EvaluationForm">
      <HookFormWrapper<APIEvaluationForm>
        initialValues={createInitialValues(
          form,
          formType as EvaluationFormTypes
        )}
        onSubmit={onSubmit}
        mode="onBlur"
      >
        {({
          control,
          handleSubmit,
          formState: { isSubmitting },
          watch,
          setValue,
        }) => {
          const values = watch();

          return (
            <>
              {!params.id && (
                <FormForm
                  control={control}
                  submitting={isSubmitting}
                  values={values}
                  setValue={setValue}
                  onSubmit={handleSubmit(onSubmit)}
                  onClose={() => {
                    if (!params.id) {
                      history.push(back || `${basePath}/performance`);
                    }
                  }}
                />
              )}
              <TopNavigationBar
                className="LearningNewSection__TopNavigation"
                breadcrumbs={[
                  { name: t('performance:tab_performance') },
                  {
                    name: t('performance:evaluation_forms'),
                    path: `${basePath}/performance`,
                  },
                  { name: values.title || t('performance:create_form') },
                ]}
                title={
                  values.title ? (
                    [<FormIcon form={values} />, values.title]
                  ) : (
                    <span className="tw-text-gray-500">
                      {t('performance:name_the_form')}
                    </span>
                  )
                }
                form={FormForm}
                formProps={{
                  control,
                  onSubmit,
                  values,
                  submitting: isSubmitting,
                  setValue,
                }}
                badge={values.status}
                tabs={[
                  {
                    name: t('performance:tab_content'),
                    to: `${basePath}/performance/forms/${params.id}/edit/components`,
                    isActive: (exact) => exact?.isExact || !!params.componentId,
                  },
                  {
                    name: t('performance:tab_settings'),
                    to: `${basePath}/performance/forms/${params.id}/edit/settings`,
                  },
                  // {
                  //   name: t('performance:tab_submissions'),
                  //   to: `/networks/${networkId}/performance/forms/${params.id}/edit/submissions`,
                  // },
                ]}
                action={
                  <>
                    <Select
                      name="status"
                      clearable={false}
                      className="FormStatusSelector !tw-mr-2"
                      value={values.status}
                      onChange={(newValue) => {
                        setValue('status', newValue?.value || newValue);
                      }}
                      options={[
                        {
                          value: 'draft',
                          label: t('common:status_draft'),
                        },
                        {
                          value: 'live',
                          label: t('common:status_published'),
                        },
                      ]}
                    />
                    <Button
                      size="large"
                      onClick={() => history.push(back || `${basePath}/performance`)}
                    >
                      {t('performance:go_back')}
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleSubmit(onSubmit)}
                      disabled={!(params.id && form)}
                      isLoading={isSubmitting}
                    >
                      {t('common:save')}
                    </Button>
                  </>
                }
              />
              {loading ? (
                <Spinner size="large" centered />
              ) : (
                <>
                  {params.id && form && (
                    <Switch>
                      <Route
                        exact
                        path={[
                          `${baseUrl}/performance/forms/:id/edit/components`,
                          `${baseUrl}/performance/forms/:id/edit/components/:componentId`,
                        ]}
                        render={(props) => (
                          <FormQuestions
                            {...props}
                            {...fns}
                            control={control}
                            setValue={setValue}
                            watch={watch}
                            basePath={basePath}
                            isSubmitting={isSubmitting}
                          />
                        )}
                      />
                      <Route
                        exact
                        path={`${baseUrl}/performance/forms/:id/edit/settings`}
                      >
                        <EvaluationFormSettings
                          setValue={setValue}
                          watch={watch}
                          currentForm={form}
                        />
                      </Route>
                      {/* <Route
                        path={[
                          '/networks/:id/performance/forms/:id/edit/submissions',
                          '/networks/:id/performance/forms/:id/edit/submissions/:submissionId',
                        ]}
                        component={EvaluationsContainer}
                      /> */}
                    </Switch>
                  )}
                </>
              )}
            </>
          );
        }}
      </HookFormWrapper>
    </Container>
  );
};

export default withPerformanceStore(EvaluationFormContainer);
